import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Copy, Download, FileJson, FileSpreadsheet, Upload, X } from 'lucide-react';

import { TOAST_AUTO_CLOSE_DURATION, greyP2, greyP3, greyP4, greyP5 } from '../../../theme.ts';
import { Lamp } from '../../../types/lamp.ts';
import { NotificationCard } from '../../notifications/NotificationCard';

interface LuminaireImportExportModalProps {
  onClose: () => void;
  onImport: (lamps: Array<{ name: string; position: [number, number, number] }>) => void;
  lamps: Lamp[];
  importEnabled: boolean;
}

export function LuminaireImportExportModal({
  onClose,
  onImport,
  lamps,
  importEnabled,
}: LuminaireImportExportModalProps) {
  const { t } = useTranslation();
  // Default to export tab if calculation has started (import disabled)
  const [activeTab, setActiveTab] = useState<'import' | 'export'>(
    !importEnabled && lamps.length > 0 ? 'export' : 'import',
  );
  const [importText, setImportText] = useState('');
  const [importFormat, setImportFormat] = useState<'json' | 'csv'>('json');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleImport = () => {
    try {
      setError('');
      let parsedLamps: Array<{ name: string; position: [number, number, number] }> = [];

      if (importFormat === 'json') {
        const data = JSON.parse(importText);

        // Handle array of lamps
        if (Array.isArray(data)) {
          parsedLamps = data.map((lamp, index) => {
            if (!lamp.name || !lamp.position) {
              throw new Error(`Lamp at index ${index} is missing required fields (name, position)`);
            }
            if (!Array.isArray(lamp.position) || lamp.position.length !== 3) {
              throw new Error(`Lamp "${lamp.name}" has invalid position format`);
            }
            return {
              name: lamp.name,
              position: [Number(lamp.position[0]), Number(lamp.position[1]), Number(lamp.position[2])] as [
                number,
                number,
                number,
              ],
            };
          });
        } else {
          throw new Error('JSON must be an array of lamps');
        }
      } else if (importFormat === 'csv') {
        const lines = importText.trim().split('\n');

        // Check if first line is header
        const hasHeader = lines[0].toLowerCase().includes('name') || lines[0].toLowerCase().includes('x');
        const dataLines = hasHeader ? lines.slice(1) : lines;

        parsedLamps = dataLines.map((line, index) => {
          const parts = line.split(',').map((part) => part.trim());

          if (parts.length < 4) {
            throw new Error(`Line ${index + 1} doesn't have enough columns (expected: name, x, y, z)`);
          }

          const [name, x, y, z] = parts;
          const xNum = Number(x);
          const yNum = Number(y);
          const zNum = Number(z);

          if (isNaN(xNum) || isNaN(yNum) || isNaN(zNum)) {
            throw new Error(`Line ${index + 1} has invalid coordinates`);
          }

          return {
            name,
            position: [xNum, yNum, zNum] as [number, number, number],
          };
        });
      }

      if (parsedLamps.length === 0) {
        throw new Error('No valid lamps found in the import data');
      }

      onImport(parsedLamps);
      const successMessage = t('workspace.importExportModal.importSuccess', { count: parsedLamps.length });
      toast(
        ({ closeToast }) => (
          <NotificationCard
            variant="success"
            title={successMessage}
            onClose={closeToast}
            autoHideDuration={TOAST_AUTO_CLOSE_DURATION}
          />
        ),
        {
          autoClose: TOAST_AUTO_CLOSE_DURATION,
          closeButton: false,
          style: { background: 'transparent', boxShadow: 'none', padding: 0 },
        },
      );
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse import data');
    }
  };

  const handleExport = (format: 'json' | 'csv') => {
    let exportData = '';

    if (format === 'json') {
      const exportLamps = lamps.map((lamp) => ({
        name: lamp.name,
        position: lamp.position,
      }));
      exportData = JSON.stringify(exportLamps, null, 2);
    } else {
      // CSV format
      exportData = 'name,x,y,z\n';
      lamps.forEach((lamp) => {
        exportData += `${lamp.name},${lamp.position[0]},${lamp.position[1]},${lamp.position[2]}\n`;
      });
    }

    navigator.clipboard.writeText(exportData).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setImportText(text);

      // Auto-detect format based on file extension
      if (file.name.endsWith('.json')) {
        setImportFormat('json');
      } else if (file.name.endsWith('.csv')) {
        setImportFormat('csv');
      }
    };
    reader.readAsText(file);
  };

  const downloadFile = (format: 'json' | 'csv') => {
    let content = '';
    let filename = '';
    let mimeType = '';

    if (format === 'json') {
      const exportLamps = lamps.map((lamp) => ({
        name: lamp.name,
        position: lamp.position,
      }));
      content = JSON.stringify(exportLamps, null, 2);
      filename = 'luminaires.json';
      mimeType = 'application/json';
    } else {
      content = 'name,x,y,z\n';
      lamps.forEach((lamp) => {
        content += `${lamp.name},${lamp.position[0]},${lamp.position[1]},${lamp.position[2]}\n`;
      });
      filename = 'luminaires.csv';
      mimeType = 'text/csv';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exampleJSON = `[
  {
    "name": "Luminaire 1",
    "position": [0, 0, 5]
  },
  {
    "name": "Luminaire 2",
    "position": [10, 0, 5]
  }
]`;

  const exampleCSV = `name,x coordinate,y coordinate,z coordinate
Luminaire 1,0,0,5
Luminaire 2,10,0,5`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="rounded-lg w-[700px] h-[650px] max-w-[90vw] max-h-[90vh] flex flex-col"
        style={{ backgroundColor: greyP4 }}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 pb-2">
          <div>
            <h2 className="text-xl font-semibold">{t('workspace.importExportModal.title')}</h2>
            <p className="text-sm text-gray-300 mt-1">{t('workspace.importExportModal.description')}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white self-start">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700 px-6">
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === 'import' ? 'border-b-2 border-lime-500 text-lime-500' : 'text-gray-400 hover:text-gray-300'
            } ${!importEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => importEnabled && setActiveTab('import')}
            disabled={!importEnabled}
            title={!importEnabled ? t('workspace.importExportModal.importDisabled') : ''}
          >
            <Upload size={16} className="inline mr-2" />
            {t('workspace.importExportModal.import')}
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === 'export' ? 'border-b-2 border-lime-500 text-lime-500' : 'text-gray-400 hover:text-gray-300'
            } ${lamps.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => setActiveTab('export')}
            disabled={lamps.length === 0}
            title={lamps.length === 0 ? t('workspace.importExportModal.noLuminairesToExport') : ''}
          >
            <Download size={16} className="inline mr-2" />
            {t('workspace.importExportModal.export')}
          </button>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Import Tab */}
          {activeTab === 'import' && (
            <div className="space-y-4">
              {/* Format Selection */}
              <div className="flex space-x-4">
                <button
                  className={`flex-1 p-3 rounded-lg border-2 ${
                    importFormat === 'json'
                      ? 'border-green01 bg-green01 bg-opacity-10'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                  onClick={() => setImportFormat('json')}
                >
                  <FileJson size={24} className="mx-auto mb-2" />
                  <div className="font-medium">JSON</div>
                </button>
                <button
                  className={`flex-1 p-3 rounded-lg border-2 ${
                    importFormat === 'csv'
                      ? 'border-green01 bg-green01 bg-opacity-10'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                  onClick={() => setImportFormat('csv')}
                >
                  <FileSpreadsheet size={24} className="mx-auto mb-2" />
                  <div className="font-medium">CSV</div>
                </button>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium mb-2">{t('workspace.importExportModal.uploadFile')}</label>
                <input
                  type="file"
                  accept={importFormat === 'json' ? '.json' : '.csv'}
                  onChange={handleFileUpload}
                  className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-green04 file:text-white hover:file:bg-green03 cursor-pointer"
                />
              </div>

              {/* Text Input */}
              <div>
                <label className="block text-sm font-medium mb-2">{t('workspace.importExportModal.pasteData')}</label>
                <textarea
                  ref={(el) => {
                    if (el) {
                      el.style.height = 'auto';
                      el.style.height = Math.max(el.scrollHeight, 52) + 'px';
                    }
                  }}
                  value={importText}
                  onChange={(e) => {
                    setImportText(e.target.value);
                    const el = e.target;
                    el.style.height = 'auto';
                    el.style.height = Math.max(el.scrollHeight, 52) + 'px';
                  }}
                  rows={2}
                  className="w-full bg-black border border-gray-600 text-white rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-green01 transition-colors duration-200"
                  style={{ resize: 'none', overflow: 'hidden', minHeight: '52px' }}
                  spellCheck={false}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-900 bg-opacity-30 border border-red-500 text-red-300 px-4 py-2 rounded">
                  {error}
                </div>
              )}

              {/* Example Format */}
              <div className="p-3 rounded text-sm" style={{ backgroundColor: greyP5 }}>
                <div className="font-medium mb-2" style={{ color: greyP2 }}>
                  {t('workspace.importExportModal.expectedFormat')}
                </div>
                <pre className="text-xs overflow-x-auto" style={{ color: greyP3 }}>
                  {importFormat === 'json' ? exampleJSON : exampleCSV}
                </pre>
              </div>
            </div>
          )}

          {/* Export Tab */}
          {activeTab === 'export' && (
            <div className="space-y-4">
              <div className="text-gray-300 mb-4">
                {t('workspace.importExportModal.exportCount', { count: lamps.length })}
              </div>

              {/* Export to Clipboard */}
              <div className="space-y-2">
                <h3 className="font-medium">{t('workspace.importExportModal.copyToClipboard')}</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleExport('json')}
                    className="flex-1 py-3 px-4 rounded-lg flex items-center justify-center space-x-2 transition-colors hover:opacity-80"
                    style={{ backgroundColor: greyP5 }}
                  >
                    <Copy size={16} />
                    <span>{t('workspace.importExportModal.copyAsJson')}</span>
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    className="flex-1 py-3 px-4 rounded-lg flex items-center justify-center space-x-2 transition-colors hover:opacity-80"
                    style={{ backgroundColor: greyP5 }}
                  >
                    <Copy size={16} />
                    <span>{t('workspace.importExportModal.copyAsCsv')}</span>
                  </button>
                </div>
                {copySuccess && (
                  <div className="text-lime-500 text-sm text-center">
                    {t('workspace.importExportModal.copiedToClipboard')}
                  </div>
                )}
              </div>

              {/* Download Files */}
              <div className="space-y-2">
                <h3 className="font-medium">{t('workspace.importExportModal.downloadFile')}</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => downloadFile('json')}
                    className="flex-1 py-3 px-4 bg-green01 hover:bg-green03 text-black rounded-lg flex items-center justify-center space-x-2 transition-colors font-medium"
                  >
                    <Download size={16} />
                    <span>{t('workspace.importExportModal.downloadJson')}</span>
                  </button>
                  <button
                    onClick={() => downloadFile('csv')}
                    className="flex-1 py-3 px-4 bg-green01 hover:bg-green03 text-black rounded-lg flex items-center justify-center space-x-2 transition-colors font-medium"
                  >
                    <Download size={16} />
                    <span>{t('workspace.importExportModal.downloadCsv')}</span>
                  </button>
                </div>
              </div>

              {/* Preview */}
              <div className="p-3 rounded" style={{ backgroundColor: greyP5 }}>
                <div className="font-medium mb-2 text-sm" style={{ color: greyP2 }}>
                  {t('workspace.importExportModal.preview')}
                </div>
                <pre className="text-xs" style={{ color: greyP3 }}>
                  {JSON.stringify(
                    lamps.map((lamp) => ({
                      name: lamp.name,
                      position: lamp.position,
                    })),
                    null,
                    2,
                  )}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Sticky bottom action button */}
        {activeTab === 'import' && (
          <div className="flex justify-end px-6 py-4 border-t border-gray-700">
            <button
              onClick={handleImport}
              disabled={!importText.trim()}
              className="px-8 py-2.5 bg-green01 hover:bg-green03 disabled:cursor-not-allowed rounded-lg font-medium text-black disabled:text-gray-400 transition-colors"
              style={!importText.trim() ? { backgroundColor: greyP3 } : undefined}
            >
              {t('workspace.importExportModal.importLuminaires')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
